import { create } from 'zustand';
import { supabase } from './supabase';
import type { Task } from './types';
import { useAuth } from './auth';

interface TaskStore {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  fetchTasks: () => Promise<void>;
  createTask: (task: Omit<Task, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateTask: (id: string, updates: Partial<Task>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
}

export const useTaskStore = create<TaskStore>((set, get) => ({
  tasks: [],
  loading: false,
  error: null,

  fetchTasks: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          task_owners!tasks_owner_id_fkey (
            id,
            name,
            email,
            organization
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching tasks:', error);
        throw error;
      }
      
      // Transform the data to match our Task type
      const transformedData = data?.map(task => ({
        ...task,
        owner: task.task_owners ? {
          id: task.task_owners.id,
          name: task.task_owners.name,
          email: task.task_owners.email,
          organization: task.task_owners.organization
        } : undefined,
        task_owners: undefined
      }));

      set({ tasks: transformedData || [], loading: false });
    } catch (error) {
      console.error('Error fetching tasks:', error);
      set({ error: error.message, loading: false });
    }
  },

  createTask: async (task) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('tasks')
        .insert([{
          ...task,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      
      await get().fetchTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateTask: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      
      // Update local state
      const tasks = get().tasks;
      const updatedTasks = tasks.map(task => 
        task.id === id 
          ? { ...task, ...updates, updated_at: new Date().toISOString() }
          : task
      );
      set({ tasks: updatedTasks });
      
      // Refetch to ensure consistency
      await get().fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  deleteTask: async (id) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      await get().fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
}));